import { getSelectList } from 'axios/dictionaryApi';
import { updateOrderAdditionalData } from 'axios/orderApi';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { OrderEditingInfo, OrderInfo } from 'models/api/order';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import '../OrderInfo.scss';
import { ClickToEditInput, Dictionary, maxRule } from '@finbeat/react-toolkit';

type OrderTabEditedFieldProps<T> = {
    input: T;
};

const LoanParametersTable = ({
    model,
    loading,
    wasOpened,
}: {
    model: OrderInfo | null | undefined;
    loading: boolean;
    wasOpened: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const [orderSettingBasketTypeDict, setOrderSettingBasketTypeDict] =
        useState<Dictionary<string> | null>(null);

    const changeInputField = (props: OrderEditingInfo) => {
        return updateOrderAdditionalData(api, model?.id, {
            ...props,
        });
    };

    useQuery(
        'orderSettingBasketType',
        () => getSelectList(api, ['OrderSettingBasketType'], i18n.language),
        {
            onSuccess: (data) => {
                data['OrderSettingBasketType'] &&
                    setOrderSettingBasketTypeDict(
                        data['OrderSettingBasketType'].reduce<Dictionary<string>>((res, x) => {
                            if (x.id && x.name) res[x.id] = x.name;
                            return res;
                        }, {})
                    );
            },
            enabled: !orderSettingBasketTypeDict && wasOpened,
        }
    );

    const { flexFinID } = useMemo(() => {
        return {
            flexFinID: { input: model?.additionalData['FlexFinID'] ?? '' },
        };
    }, [model]);

    return (
        <table className="table table-sm mb-0">
            <tbody>
                <tr>
                    <td>{t('BackOfficeOrderDate')}</td>
                    <td className={loading ? 'loading td_loading' : ''}>
                        <DataTimeText value={model?.sd} />
                    </td>
                </tr>
                <tr>
                    <td>{t('BackOfficeOrderStatus')}</td>
                    <td className={loading ? 'loading td_loading' : ''}>{model?.statusName}</td>
                </tr>
                <tr>
                    <td>{t('BackOfficeProductType')}</td>
                    <td className={loading ? 'loading td_loading' : ''}>{model?.productType}</td>
                </tr>

                {model?.dataForCC && (
                    <>
                        <tr>
                            <td>{t('BackOfficeInterestPercent')}</td>
                            <td className={loading ? 'loading td_loading' : ''}>
                                <Money value={model?.dataForCC.interestPercent} />
                            </td>
                        </tr>
                        <tr>
                            <td>{t('BackOfficeUpFrontFeePercent')}</td>
                            <td className={loading ? 'loading td_loading' : ''}>
                                <Money value={model?.dataForCC.upFrontFeePercent} />
                            </td>
                        </tr>
                        <tr>
                            <td>{t('BackOfficeDailyPlatformFee')}</td>
                            <td className={loading ? 'loading td_loading' : ''}>
                                <Money value={model?.dataForCC.dailyPlatformFee} />
                            </td>
                        </tr>
                    </>
                )}

                {model?.additionalData['Term'] && (
                    <tr>
                        <td>{t('BackOfficeTerm')}</td>
                        <td className={loading ? 'loading td_loading' : ''}>
                            {model?.additionalData['Term']}
                        </td>
                    </tr>
                )}

                {model?.additionalData['ClientRequestAmount'] && (
                    <tr>
                        <td>{t('BackOfficeRequestedAmount')}</td>
                        <td className={loading ? 'loading td_loading' : ''}>
                            <Money value={model?.additionalData['ClientRequestAmount']} />
                        </td>
                    </tr>
                )}

                {model?.additionalData['ApprovedMaxAmount'] && (
                    <tr>
                        <td>{t('BackOfficeApprovedAmount')}</td>
                        <td className={loading ? 'loading td_loading' : ''}>
                            <Money value={model?.additionalData['ApprovedMaxAmount']} />
                        </td>
                    </tr>
                )}
                {model?.status === 'goodship' && (
                    <tr>
                        <td className="align-middle">{t('BackOfficeFlexFinID')}</td>
                        <td className={loading ? 'loading td_loading' : ''}>
                            <ClickToEditInput<OrderTabEditedFieldProps<string>>
                                name="input"
                                mutationFunction={({ values }) =>
                                    changeInputField({
                                        flexFinID: values.input.toString() || '',
                                    })
                                }
                                className="order-editable-fixed-height"
                                isFloating={false}
                                loading={loading}
                                disabled={!hasRole('ordersUpdateAdditionalData')}
                                defaultValues={flexFinID}
                                rules={{
                                    valueAsNumber: true,
                                    maxLength: maxRule(10),
                                }}
                            />
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

export default LoanParametersTable;
